import { Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { RecordAccessGrant } from '@econtractor/types';
import { UserNameItem, UserSelected } from 'modules/user';
import SolarIcon from 'components/icons/SolarIcon';
import SpinnerBlock from 'components/others/SpinnerBlock';
import AccessUserList from './AccessUserList';
import useRecordAccess, { type RecordAccessSave } from './model/useRecordAccess';

export interface RecordAccessOffcanvasProps {
    show: boolean;
    setShow: (show: boolean) => void;
    grants: RecordAccessGrant[];
    canManage: boolean;
    /** Власник лишається як є. Його не додаємо в списки доступів і не змінюємо. */
    ownerId?: number;
    ownerName?: string | null;
    ownerEmail?: string | null;
    ownerPicture?: string | null;
    onSave: RecordAccessSave;
}

const RecordAccessOffcanvas = ({
    show,
    setShow,
    grants,
    canManage,
    ownerId = 0,
    ownerName,
    ownerEmail,
    ownerPicture,
    onSave,
}: RecordAccessOffcanvasProps) => {
    const { t } = useTranslation(['common']);
    const {
        busy,
        editRows,
        viewRows,
        takenIds,
        addUser,
        removeUser,
    } = useRecordAccess({ grants, canManage, ownerId, onSave });

    return (
        <Offcanvas
            show={show}
            onHide={() => {
                if (!busy) setShow(false);
            }}
            placement="end"
            container={typeof document !== 'undefined' ? document.body : undefined}
        >
            <Offcanvas.Header closeButton className="border-bottom">
                <Offcanvas.Title>{t('Users.accessGrant')}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative">
                <SpinnerBlock onlyBlur show={busy} />
                <section className="access-grant-section">
                    <h6 className="access-grant-section__title mb-2">
                        <SolarIcon name="person" />
                        {t('Users.accessOwner')}
                    </h6>
                    {ownerId > 0 ? (
                        <UserNameItem
                            smallMode
                            user={{
                                ID: ownerId,
                                userName: ownerName || '',
                                userEmail: ownerEmail || '',
                                userPicture: ownerPicture || '',
                            }}
                        />
                    ) : (
                        <div className="text-muted">—</div>
                    )}
                </section>

                <section className="access-grant-section">
                    <AccessUserList
                        title={t('Users.accessLevelEdit')}
                        icon="pencil"
                        rows={editRows}
                        canManage={canManage}
                        busy={busy}
                        onRemove={removeUser}
                    />
                    {canManage ? (
                        <UserSelected
                            smallMode
                            clearable={false}
                            role="seller"
                            excludeIds={takenIds}
                            title={t('Users.accessAdd')}
                            onChange={(user) => addUser('edit', user)}
                        />
                    ) : null}
                </section>

                <section className="access-grant-section">
                    <AccessUserList
                        title={t('Users.accessLevelView')}
                        icon="eye"
                        rows={viewRows}
                        canManage={canManage}
                        busy={busy}
                        onRemove={removeUser}
                    />
                    {canManage ? (
                        <UserSelected
                            smallMode
                            clearable={false}
                            role="seller"
                            excludeIds={takenIds}
                            title={t('Users.accessAdd')}
                            onChange={(user) => addUser('view', user)}
                        />
                    ) : null}
                </section>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default RecordAccessOffcanvas;
